import type { HttpClient, QueryParams } from "./http";

/** Ids per findByIds request; see findByIds. */
const FIND_BY_IDS_BATCH = 100;

/**
 * What a grid asks a paged endpoint for. The TypeScript twin of the Java
 * `PageQuery`.
 *
 * The names in `sort` and `filters` are the ones the repository publishes, and
 * a table's column ids are expected to match them; anything else is a 400
 * rather than a silently ignored parameter, so a renamed column fails at once
 * instead of showing the wrong rows.
 *
 * Until an endpoint supports paging, `findAll()` remains available and behaves
 * exactly like today's `GET /{path}`.
 */
export interface PageRequest {
  /** Zero-based. */
  page?: number;
  /** Rows per page. The server defaults to 100 and caps at 1000. */
  size?: number;
  /** `["code,asc", "created_at,desc"]`, applied in order. */
  sort?: string[];
  /** Free-text search across the endpoint's searchable columns. */
  q?: string;
  /**
   * Field name to its values. Several values mean "any of"; exactly two mean a
   * `[from, to]` range, where either side may be blank for an open bound.
   */
  filters?: Record<string, readonly string[]>;
  /**
   * Whether to compute the match count and the aggregates — the expensive half
   * of a page, and unchanged while scrolling one query. Defaults to true;
   * `useServerTable` sends false for every page after the first.
   */
  total?: boolean;
}

export interface Page<T> {
  content: T[];
  page: number;
  size: number;
  /** Rows matching the query, or null when the request declined to count them. */
  total: number | null;
  /** Endpoint-specific totals over the same matching rows — "active: 9211". */
  aggregates?: Record<string, number>;
}

/** The filter and search half of a `PageRequest` — what identifies a result set. */
export type MatchRequest = Omit<PageRequest, "page" | "size" | "total">;

/**
 * The TypeScript twin of the Java `AControllerClient<T>` — the generic surface
 * every `AController` subclass inherits. Module clients extend this and add
 * their bespoke endpoints, exactly as the Java clients do.
 */
export class ResourceClient<T, TId = number> {
  constructor(
    protected readonly http: HttpClient,
    /** Path segment without a trailing slash, e.g. `/product`. */
    protected readonly path: string,
  ) {}

  findAll(): Promise<T[]> {
    return this.http.get<T[]>(this.path);
  }

  /**
   * One page of rows. Discriminated on the server by the presence of `page`,
   * so this and `findAll()` share a path and neither disturbs the other.
   */
  find(request: PageRequest = {}): Promise<Page<T>> {
    // `page` is what tells the server this is the paged endpoint rather than
    // find-all, so it is always sent — an omitted one would quietly fetch the
    // entire table.
    return this.http.get<Page<T>>(this.path, {
      query: pageParams({ ...request, page: request.page ?? 0 }),
    });
  }

  /**
   * The ids of every row matching the filters and search, ignoring paging.
   *
   * This is what "select all matching" selects once the rows are no longer all
   * in the browser: ids alone are a fraction of the payload, and every batch
   * endpoint takes ids anyway.
   */
  findMatchingIds(request: MatchRequest = {}): Promise<number[]> {
    return this.http.get<number[]>(`${this.path}/ids`, { query: pageParams(request) });
  }

  findById(id: TId): Promise<T | null> {
    return nullOnNotFound(this.http.get<T>(`${this.path}/${String(id)}`));
  }

  /**
   * Fetched 100 ids per request: every id rides in the query string, and a
   * few thousand of them overflow the server's request-header limit (the
   * legacy production grid lost every thumbnail to that).
   */
  async findByIds(ids: TId[]): Promise<T[]> {
    if (ids.length === 0) return [];
    const batches: TId[][] = [];
    for (let i = 0; i < ids.length; i += FIND_BY_IDS_BATCH) batches.push(ids.slice(i, i + FIND_BY_IDS_BATCH));
    const pages = await Promise.all(
      batches.map((batch) => this.http.get<T[]>(this.path, { query: { ids: batch.map(String) } })),
    );
    return pages.flat();
  }

  count(): Promise<number> {
    return this.http.get<number>(`${this.path}/count`);
  }

  existsById(id: TId): Promise<boolean> {
    return this.http.get<boolean>(`${this.path}/exists/${String(id)}`);
  }

  create(entity: T): Promise<T> {
    return this.http.post<T>(this.path, entity);
  }

  createBatch(entities: T[]): Promise<T[]> {
    return this.http.post<T[]>(`${this.path}/batch`, entities);
  }

  /** The backend answers 204 with no body; success is the absence of an error. */
  async update(entity: T): Promise<void> {
    await this.http.put<void>(this.path, entity, { responseType: "void" });
  }

  async updateBatch(entities: T[]): Promise<void> {
    await this.http.put<void>(`${this.path}/batch`, entities, { responseType: "void" });
  }

  async remove(id: TId): Promise<void> {
    await this.http.delete<void>(`${this.path}/${String(id)}`, { responseType: "void" });
  }

  async removeBatch(ids: TId[]): Promise<void> {
    await this.http.request<void>(`${this.path}/batch`, {
      method: "DELETE",
      body: ids,
      responseType: "void",
    });
  }
}

/**
 * A single-entity lookup whose miss is an answer, not a failure.
 *
 * The server says 404 for "no artwork has this code" and "no default store is
 * set" alike, and a client method typed `T | null` promises to say null. It
 * has to actually catch the 404 to keep that promise: an artwork rename once
 * failed on every unused code, because the existence check threw instead of
 * answering null. Every other status still propagates.
 */
export async function nullOnNotFound<T>(request: Promise<T>): Promise<T | null> {
  try {
    return await request;
  } catch (error) {
    if (isNotFound(error)) return null;
    throw error;
  }
}

/**
 * Flattens a page request into query parameters: `filters` becomes one
 * `filter.<name>` entry per field, and the repeated-value encoding is left to
 * `buildQuery`.
 */
function pageParams(request: PageRequest): QueryParams {
  const { filters, sort, ...rest } = request;
  const params: QueryParams = { ...rest };
  if (sort?.length) params.sort = sort;
  for (const [name, values] of Object.entries(filters ?? {})) {
    if (values.length > 0) params[`filter.${name}`] = values;
  }
  return params;
}

function isNotFound(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status: unknown }).status === 404
  );
}
